import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import api, { getStorageUrl } from '../../api/axios';
import {
  ChevronLeft,
  Pencil,
  FileText,
  Calendar,
  Wallet,
  User as UserIcon,
  Briefcase,
  ExternalLink,
  Download,
  Loader2,
  Building,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  IndianRupee,
  Image as ImageIcon,
  FileCheck,
  XCircle,
  MessageSquare,
  X
} from 'lucide-react';
import styles from './ViewExpense.module.css';
import toast from 'react-hot-toast';
import NotFound from '../Error/NotFound';


const ViewExpense = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expense, setExpense] = useState(null);
  const [histories, setHistories] = useState([]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(''); // 'approve' or 'reject'
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user'));
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/expenses/${id}`);
      if (response.data.success) {
        setExpense(response.data.data);
        fetchHistory(); // Fetch history as well
      }
    } catch (error) {
      console.error('Error fetching expense:', error);
      toast.error('Failed to load expense details');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get(`/expenses/${id}/history`);
      if (response.data.success) {
        setHistories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const openActionModal = (action) => {
    setModalAction(action);
    setReason('');
    setIsModalOpen(true);
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    if (!reason || reason.length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post(`/expenses/${id}/${modalAction}`, { reason });
      if (response.data.success) {
        toast.success(`Expense ${modalAction === 'approve' ? 'approved' : 'rejected'} successfully`);
        setIsModalOpen(false);
        fetchExpense(); // Refresh data
      }
    } catch (error) {
      console.error(`Error ${modalAction}ing expense:`, error);
      toast.error(error.response?.data?.message || `Failed to ${modalAction} expense`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles['view-expense-container']}>
        <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
        <div className={styles['content-wrapper']}>
          <Header setIsSidebarOpen={setIsSidebarOpen} />
          <div className={styles['loading-container']}>
            <Loader2 size={48} className={styles['spinner']} />
            <p>Fetching technical details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!expense) {
    return <NotFound />;
  }


  const isImage = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp'].includes(ext);
  };

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'approved') return styles['approved'];
    if (s === 'rejected') return styles['rejected'];
    return styles['pending'];
  };

  return (
    <div className={`${styles['view-expense-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-left']}>
              <Breadcrumbs items={[
                { label: 'Finance', path: '/expenses' },
                { label: 'Expenses', path: '/expenses' },
                { label: 'View Details', path: `/expenses/view/${id}` }
              ]} />
              <h1>{expense.expense_id || `EXP-${expense.id}`}</h1>
            </div>

            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/expenses')}>
                <ChevronLeft size={18} />
                <span>Back</span>
              </button>

              {isAdmin && (
                <>
                  <button
                    className={`${styles['btn-action']} ${styles['approve']}`}
                    onClick={() => openActionModal('approve')}
                  >
                    <CheckCircle2 size={18} />
                    <span>Approve</span>
                  </button>
                  <button
                    className={`${styles['btn-action']} ${styles['reject']}`}
                    onClick={() => openActionModal('reject')}
                  >
                    <XCircle size={18} />
                    <span>Reject</span>
                  </button>
                </>
              )}

              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Download size={18} />
                <span>Export Details</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => navigate(`/expenses/edit/${id}`)}>
                <Pencil size={18} />
                <span>Edit Expense</span>
              </button>
            </div>
          </div>

          <div className={styles['expense-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>{expense.expense_id || `EXP-${expense.id}`}</h1>
                <span className={`${styles['status-badge']} ${getStatusClass(expense.status)}`}>
                  <span className={styles['dot']}></span>
                  {(expense.status || 'pending').toUpperCase()}
                </span>
              </div>

              <div className={styles['expense-meta']}>
                <div className={styles['meta-item']}>
                  <Building size={16} />
                  <span>{expense.project?.project_name || 'General Business'}</span>
                </div>
                <div className={styles['meta-item']}>
                  <Wallet size={16} />
                  <span>{expense.category?.category_name}</span>
                </div>
                <div className={styles['meta-item']}>
                  <Calendar size={16} />
                  <span>{new Date(expense.expense_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><IndianRupee size={20} /></div>
                <span className={styles['m-trend']}>TOTAL</span>
              </div>
              <div className={styles['m-info']}>
                <label>Amount Spent</label>
                <h2 style={{ color: '#f97316' }}>₹{parseFloat(expense.amount).toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-cat']}`}><FileText size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Category</label>
                <h2>{expense.category?.category_name || 'N/A'}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-emp']}`}><UserIcon size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Incurred By</label>
                <h2 style={{ color: '#15803d' }}>{expense.employee?.employee_name?.split(' ')[0] || 'N/A'}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-date']}`}><Calendar size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Expense Date</label>
                <h2>{new Date(expense.expense_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</h2>
              </div>
            </div>
          </div>

          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><CreditCard size={20} /><h3>Transaction Details</h3></div>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Date of Expense</span>
                    <span className={styles['d-value']}>{new Date(expense.expense_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Category</span>
                    <span className={styles['d-value']}>{expense.category?.category_name || 'Uncategorized'}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Employee (Incurred By)</span>
                    <span className={styles['d-value']}>{expense.employee?.employee_name || 'N/A'}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Linked Project</span>
                    <span className={styles['d-value']}>{expense.project?.project_name || 'General Business'}</span>
                  </div>
                  <div className={styles['detail-item']} style={{ borderBottom: 'none', paddingTop: '16px', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                    <span className={styles['d-label']}>Description</span>
                    <p style={{ margin: 0, fontSize: '14px', color: '#475569', lineHeight: 1.6, fontWeight: 500 }}>
                      {expense.description || 'No description provided.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><CheckCircle2 size={20} /><h3>Audit Trail</h3></div>
                </div>
                <div className={styles['history-table-wrapper']}>
                  <table className={styles['history-table']}>
                    <thead>
                      <tr>
                        <th>DATE & TIME</th>
                        <th>ACTION BY</th>
                        <th>STATUS</th>
                        <th>REASON / COMMENT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {histories.length > 0 ? (
                        histories.map((item, idx) => (
                          <tr key={idx}>
                            <td className={styles['hist-date-cell']}>
                              {new Date(item.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td>
                              <div className={styles['hist-user-info']}>
                                <div className={styles['hist-avatar']}>{item.changed_by?.name?.charAt(0) || 'S'}</div>
                                <span>{item.changed_by?.name || 'System'}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`${styles['hist-status-badge']} ${getStatusClass(item.new_status)}`}>
                                {item.new_status.toUpperCase()}
                              </span>
                            </td>
                            <td className={styles['hist-reason-cell']}>{item.reason}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className={styles['hist-date-cell']}>
                            {new Date(expense.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td>
                            <div className={styles['hist-user-info']}>
                              <div className={styles['hist-avatar']}>{expense.creator?.name?.charAt(0) || 'S'}</div>
                              <span>{expense.creator?.name || 'System User'}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`${styles['hist-status-badge']} ${styles['pending']}`}>CREATED</span>
                          </td>
                          <td className={styles['hist-reason-cell']}>Initial Record Creation</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><FileCheck size={20} /><h3>Supporting Document</h3></div>
                  {expense.bill_filepath && (
                    <a
                      href={getStorageUrl(expense.bill_filepath)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#f97316', fontSize: '12px', fontWeight: 800, textDecoration: 'none' }}
                    >
                      VIEW FULL
                    </a>
                  )}
                </div>
                {!expense.bill_filepath ? (
                  <div className={styles['document-preview-box']} style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <ImageIcon size={48} color="#cbd5e1" />
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', margin: 0 }}>No supporting document attached.</p>
                  </div>
                ) : isImage(expense.bill_filename) ? (
                  <div className={styles['document-preview-box']}>
                    <img
                      src={getStorageUrl(expense.bill_filepath)}
                      alt="Receipt"
                      className={styles['preview-image']}
                    />
                  </div>
                ) : (
                  <div className={styles['document-preview-box']} style={{ background: '#f0f9ff', border: '1px solid #e0f2fe' }}>
                    <FileCheck size={48} color="#0369a1" />
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>PDF Attachment</p>
                      <p style={{ fontSize: '11px', color: '#0369a1', fontWeight: 700 }}>{expense.bill_filename}</p>
                    </div>
                    <a
                      href={getStorageUrl(expense.bill_filepath)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles['btn-secondary']}
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      Download Document
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Footer />
        </main>
      </div>

      {/* Action Modal */}
      {isModalOpen && (
        <div className={styles['modal-overlay']} onClick={() => setIsModalOpen(false)}>
          <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
            <div className={styles['modal-header']}>
              <div className={styles['modal-title-wrapper']}>
                <div className={`${styles['modal-icon']} ${modalAction === 'approve' ? styles['bg-approve'] : styles['bg-reject']}`}>
                  {modalAction === 'approve' ? <CheckCircle2 size={24} /> : <XCircle size={24} />}
                </div>
                <div>
                  <h3>{modalAction === 'approve' ? 'Approve Expense' : 'Reject Expense'}</h3>
                  <p>Provide a reason for this decision</p>
                </div>
              </div>
              <button className={styles['modal-close']} onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className={styles['modal-body-wrapper']}>
              {histories.length > 0 && (
                <div className={styles['modal-history-section']}>
                  <h4>Previous History</h4>
                  <div className={styles['m-hist-table']}>
                    <div className={styles['m-hist-thead']}>
                      <div className={styles['m-hist-th']}>DATE</div>
                      <div className={styles['m-hist-th']}>USER</div>
                      <div className={styles['m-hist-th']}>STATUS</div>
                      <div className={styles['m-hist-th']}>REASON</div>
                    </div>
                    <div className={styles['m-hist-tbody']}>
                      {histories.map((item, idx) => (
                        <div key={idx} className={styles['m-hist-tr']}>
                          <div className={styles['m-hist-td']} data-label="DATE">{new Date(item.created_at).toLocaleDateString()}</div>
                          <div className={styles['m-hist-td']} data-label="USER">{item.changed_by?.name}</div>
                          <div className={styles['m-hist-td']} data-label="STATUS">
                            <span className={`${styles['m-hist-badge']} ${getStatusClass(item.new_status)}`}>{item.new_status}</span>
                          </div>
                          <div className={styles['m-hist-td']} data-label="REASON">{item.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleActionSubmit} className={styles['modal-form']}>
                <div className={styles['modal-body-padding']}>
                  <div className={styles['form-group']}>
                    <label>
                      <MessageSquare size={16} />
                      Reason / Comments <span style={{ color: 'red' }}>*</span>
                    </label>
                    <textarea
                      placeholder={`Enter reason for ${modalAction === 'approve' ? 'approval' : 'rejection'}...`}
                      rows={4}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className={styles['modal-footer']}>
                  <button type="button" className={styles['btn-modal-secondary']} onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${styles['btn-modal-primary']} ${modalAction === 'approve' ? styles['btn-approve'] : styles['btn-reject']}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 size={18} className={styles['spinner']} />
                    ) : (
                      modalAction === 'approve' ? <CheckCircle2 size={18} /> : <XCircle size={18} />
                    )}
                    <span>{isSubmitting ? 'Processing...' : modalAction === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewExpense;
